/**
 * ProductGallery Component
 * Ported from afrodita-components - Simplified for template-project
 * Desktop gallery with vertical thumbnails and main image display
 */

import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
  type HTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import InnerImageZoom from 'react-inner-image-zoom';
import { Image as ImageIcon, ZoomIn, X } from 'lucide-react';
import 'react-inner-image-zoom/lib/styles.min.css';
import { defaultIconProps } from '../atoms/Icon';
import { lockBodyScroll, unlockBodyScroll } from '../utils/body-scroll-lock';

export interface ProductGalleryImage {
  url: string;
  key: string;
  alt?: string;
  width?: number;
  height?: number;
  isVideo?: boolean;
  posterUrl?: string;
  mimeType?: string;
  zoomUrl?: string;
}

export interface ZoomConfig {
  enabled?: boolean;
  zoomType?: 'hover' | 'click';
  hideHint?: boolean;
  zoomScale?: number;
}

export interface ProductGalleryLabels {
  productImages?: string;
  zoomInOnProductImage?: string;
  zoomIn?: string;
  productImagePagination?: string;
  viewImage?: (index: number, total: number) => string;
  productImageZoom?: string;
  zoomOut?: string;
  closeZoom?: string;
  productImage?: string;
  productImageZoomed?: string;
}

const DEFAULT_GALLERY_LABELS: Required<
  Pick<
    ProductGalleryLabels,
    | 'productImages'
    | 'zoomInOnProductImage'
    | 'zoomIn'
    | 'productImagePagination'
    | 'productImageZoom'
    | 'zoomOut'
    | 'closeZoom'
    | 'productImage'
    | 'productImageZoomed'
  >
> & { viewImage: (index: number, total: number) => string } = {
  productImages: 'Product images',
  zoomInOnProductImage: 'Zoom in on product image',
  zoomIn: 'Zoom in',
  productImagePagination: 'Product image pagination',
  viewImage: (index, total) => `View image ${index + 1} of ${total}`,
  productImageZoom: 'Product image zoom',
  zoomOut: 'Zoom out',
  closeZoom: 'Close zoom',
  productImage: 'Product image',
  productImageZoomed: 'Product image zoomed',
};

function resolveGalleryLabels(labels?: ProductGalleryLabels) {
  return {
    ...DEFAULT_GALLERY_LABELS,
    ...labels,
    viewImage: labels?.viewImage ?? DEFAULT_GALLERY_LABELS.viewImage,
  };
}

export interface ProductGalleryProps {
  images: ProductGalleryImage[];
  selectedValue?: ProductGalleryImage;
  topTag?: React.ReactNode;
  topRightTag?: React.ReactNode;
  bottomTag?: React.ReactNode;
  thumbnailPosition?: 'vertical' | 'horizontal';
  /** `carousel`: full-width swipe gallery with dots (PDP mobile). `thumbnails`: default desktop layout. */
  layout?: 'thumbnails' | 'carousel';
  hasToPreserveOrder?: boolean;
  zoomConfig?: ZoomConfig;
  /** Localized aria labels and control text (storefront passes translated strings). */
  labels?: ProductGalleryLabels;
}

const GalleryContainer = styled.div<{ thumbnailPosition: 'vertical' | 'horizontal'; showThumbnails: boolean }>`
  display: grid;
  grid-template-columns: ${({ thumbnailPosition, showThumbnails }) =>
    thumbnailPosition === 'horizontal' || !showThumbnails ? '1fr' : '80px 1fr'};
  grid-column-gap: 12px;
  width: 100%;
  max-width: 670px;
  /* Let the 1fr column shrink so inline-block .iiz still gets a bounded width (grid default min-width: auto). */
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    max-width: none;
  }
`;

const ThumbnailList = styled.div<{ thumbnailPosition: 'vertical' | 'horizontal' }>`
  display: flex;
  flex-direction: ${({ thumbnailPosition }) => (thumbnailPosition === 'horizontal' ? 'row' : 'column')};
  gap: ${({ theme }) => theme.space.sm};
  overflow-y: ${({ thumbnailPosition }) => (thumbnailPosition === 'vertical' ? 'auto' : 'visible')};
  overflow-x: ${({ thumbnailPosition }) => (thumbnailPosition === 'horizontal' ? 'auto' : 'visible')};
  max-height: ${({ thumbnailPosition }) => (thumbnailPosition === 'vertical' ? '100%' : 'auto')};
  padding: ${({ theme }) => `${theme.space.xs} 0`};

  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.colors.border.color} transparent`};

  &::-webkit-scrollbar {
    width: ${({ theme }) => theme.space.xs};
    height: ${({ theme }) => theme.space.xs};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.border.color};
    border-radius: ${({ theme }) => theme.radius.small};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: visible;
    order: 2;
    padding: ${({ theme }) => `${theme.space.sm} 0`};
  }
`;

const ThumbnailButton = styled.button<{ isActive: boolean }>`
  border-width: ${({ isActive }) => (isActive ? '2px' : '1px')};
  border-style: solid;
  border-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.text.primary : theme.colors.border.color};
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  background: transparent;
  transition: all 0.2s;
  flex-shrink: 0;
  width: 64px;
  height: 80px;
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus-visible {
    outline-width: ${({ theme }) => theme.space.xs};
    outline-style: solid;
    outline-color: ${({ theme }) => theme.colors.primary};
    outline-offset: ${({ theme }) => theme.space.xs};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 48px;
    height: 60px;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.tertiary};
  
  svg {
    width: 16px;
    height: 16px;
    opacity: 0.5;
  }
`;

const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background.secondary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    order: 1;
  }
`;

const CarouselRoot = styled.div`
  width: 100%;
  max-width: none;
`;

const GallerySlideAnnouncer = styled.p`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const CarouselGalleryFrame = styled.div`
  position: relative;
  width: 100%;
`;

const CarouselViewport = styled.div`
  display: flex;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  touch-action: pan-x;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CarouselSlide = styled.div`
  flex: 0 0 100%;
  width: 100%;
  min-width: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
`;

const CarouselDots = styled.div`
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: 0;
  pointer-events: auto;
`;

const CarouselDotHitArea = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const CarouselDot = styled.span<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '10px' : '8px')};
  height: ${({ $active }) => ($active ? '10px' : '8px')};
  border-radius: 50%;
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.border.color};
  transition: width 0.2s ease, height 0.2s ease, background-color 0.2s ease;
  pointer-events: none;
`;

const CarouselZoomButton = styled.button<{ $visible: boolean }>`
  position: absolute;
  right: 0;
  bottom: calc(${({ theme }) => theme.space.md} + 40px);
  z-index: 4;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 8px 10px;
  border: none;
  border-radius: 9999px 0 0 9999px;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  box-shadow: ${({ theme }) => theme.shadow?.sm || '-2px 2px 10px rgba(0, 0, 0, 0.12)'};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.15s ease;

  &:active,
  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const FullscreenZoomOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: ${({ theme }) => theme.colors.shades?.black?.color || '#000000'};
  touch-action: none;
  user-select: none;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const FullscreenZoomStage = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const FullscreenZoomPanLayer = styled.div<{ $x: number; $y: number }>`
  transform: translate(${({ $x, $y }) => `${$x}px, ${$y}px`});
  will-change: transform;
`;

const FullscreenZoomImage = styled.img<{ $scale: number }>`
  display: block;
  width: 100vw;
  max-width: none;
  height: auto;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: center center;
  pointer-events: none;
`;

const FullscreenZoomCloseButton = styled.button`
  position: fixed;
  left: 50%;
  bottom: 40px;
  transform: translateX(-50%);
  z-index: 100001;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  border: none;
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  font-weight: 600;
  box-shadow: ${({ theme }) => theme.shadow?.lg || '0 4px 20px rgba(0, 0, 0, 0.45)'};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active,
  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.background.primary};
    outline-offset: 2px;
  }
`;

const FullscreenZoomControls = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 100001;
  display: flex;
  gap: 8px;
`;

const FullscreenZoomControlButton = styled.button`
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 20px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadow?.md || '0 2px 12px rgba(0, 0, 0, 0.35)'};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.background.primary};
    outline-offset: 2px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.tertiary};
  
  svg {
    width: 64px;
    height: 64px;
    opacity: 0.5;
  }
`;

const TagContainer = styled.div<{ position: 'top' | 'topRight' | 'bottom' }>`
  position: absolute;
  z-index: 2;

  ${({ position, theme }) => {
    switch (position) {
      case 'top':
        return `
          top: ${theme.space.md};
          left: ${theme.space.md};
        `;
      case 'topRight':
        return `
          top: ${theme.space.md};
          right: ${theme.space.md};
        `;
      case 'bottom':
        return `
          bottom: ${theme.space.md};
          left: ${theme.space.md};
        `;
    }
  }}
`;

const StyledInnerImageZoom = styled(InnerImageZoom)`
  max-width: 100%;
`;

const ImageZoomContainer = styled.div`
  .iiz {
    max-width: 100%;
    margin: 0;
    position: relative;
    overflow: hidden;
    display: inline-block;
    cursor: zoom-in;
  }

  .iiz.zoomWrapper {
    vertical-align: top;
  }

  .iiz--drag .iiz__zoom-img--visible {
    cursor: grab;
  }

  .iiz__img {
    max-width: 100%;
    height: auto;
    display: block;
    pointer-events: none;
    visibility: visible;
    opacity: 1;
  }

  .iiz__img--hidden {
    visibility: hidden;
    opacity: 0;
  }

  .iiz__img--abs {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: block;
  }

  .iiz__zoom-img {
    width: auto !important;
    max-width: none !important;
    position: absolute;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    display: block;
  }

  .iiz__zoom-img--visible {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
    cursor: zoom-out;
    touch-action: none;
    z-index: 10;
  }

  .iiz__zoom-portal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 99999;
    background: ${({ theme }) => theme.colors.shades.black.color};
    overflow: hidden;
    touch-action: none;
  }

  .iiz__zoom-portal .iiz__zoom-img {
    max-width: none !important;
    max-height: none !important;
    cursor: grab !important;
    user-select: none !important;
    pointer-events: auto !important;
  }

  .iiz__zoom-portal .iiz__zoom-img--visible {
    visibility: visible !important;
    opacity: 1 !important;
    cursor: grab !important;
    pointer-events: auto !important;
    touch-action: none !important;
  }

  .iiz__zoom-portal .iiz__zoom-img--visible:active {
    cursor: grabbing !important;
  }

  .iiz__btn {
    background: ${({ theme }) => `${theme.colors.shades.white.color}cc`};
    max-width: 95px;
    min-width: fit-content;
    height: 32px;
    border: none;
    padding: 0 8px;
    position: absolute;
    z-index: 5;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
  }

  .iiz__btn:before {
    content: ' ';
    background-position: center;
    background-repeat: no-repeat;
    display: block;
  }

  .iiz__hint {
    bottom: 10%;
    right: 0;
    pointer-events: none;
    border-radius: 150px 0 0 150px;
  }

  .iiz__hint:before {
    content: '+';
    background-image: none;
    width: 17px;
    height: 17px;
    font-size: 19px;
    border-radius: 500px;
    border: 2px solid ${({ theme }) => theme.colors.text.primary};
    display: inline-block;
    position: relative;
    text-align: center;
    line-height: 17px;
    font-weight: 600;
  }

  .iiz__hint:after {
    content: 'Zoom in';
    font-size: 14px;
    font-weight: 600;
    display: inline-block;
    margin-left: 5px;
  }

  .iiz__close {
    border-radius: 25px;
    position: fixed !important;
    top: auto !important;
    right: auto !important;
    left: 50% !important;
    bottom: 40px !important;
    transform: translateX(-50%) !important;
    margin: 0 !important;
    visibility: hidden;
    opacity: 0;
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => `${theme.colors.background.primary}f2`} !important;
    padding: 12px 20px !important;
    box-shadow: 0 4px 20px ${({ theme }) => `${theme.colors.shades.black.color}80`} !important;
    backdrop-filter: blur(10px) !important;
    z-index: 100001 !important;
  }

  .iiz__close--visible {
    visibility: visible;
    opacity: 1;
    cursor: pointer;
  }

  .iiz__close::before {
    content: ' ';
    width: 16px;
    height: 16px;
    background-image: linear-gradient(${({ theme }) => theme.colors.text.primary}, ${({ theme }) => theme.colors.text.primary}), linear-gradient(${({ theme }) => theme.colors.text.primary}, ${({ theme }) => theme.colors.text.primary});
    background-size: 65% 2px, 2px 65%;
    transform: rotate(45deg);
    border-radius: 500px;
    border: 2px solid ${({ theme }) => theme.colors.text.primary};
  }

  .iiz__close::after {
    content: 'Close';
    font-size: 14px;
    font-weight: 600;
    display: inline-block;
    margin-left: 5px;
  }

  @media only screen and (max-width: 768px) {
    .iiz.zoomWrapper > div {
      height: auto;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }
`;

// Placeholder icon component for missing images
const PlaceholderIcon = () => (
  <ImageIcon {...defaultIconProps} size={64} strokeWidth={1} />
);


const ThumbnailVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  pointer-events: none;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  border: 0;
  display: block;
`;

const YOUTUBE_REGEX = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
const VIMEO_REGEX = /(?:vimeo\.com\/)(\d+)/;

function isExternalVideoUrl(url: string): boolean {
  return YOUTUBE_REGEX.test(url) || VIMEO_REGEX.test(url);
}

function getEmbedUrl(url: string): string | null {
  const ytMatch = YOUTUBE_REGEX.exec(url);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1`;

  const vimeoMatch = VIMEO_REGEX.exec(url);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1`;

  return null;
}

interface LazyVideoProps {
  src: string;
  poster?: string;
  isActive: boolean;
  shouldLoad: boolean;
}

const LazyVideo = memo(({ src, poster, isActive, shouldLoad }: LazyVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      video.play().catch(() => {
        // Autoplay may be blocked by browser policy
      });
    } else {
      video.pause();
    }
  }, [isActive]);

  return (
    <StyledVideo
      ref={videoRef}
      src={shouldLoad ? src : undefined}
      poster={poster}
      muted
      loop
      playsInline
      preload={shouldLoad ? 'metadata' : 'none'}
    />
  );
});

LazyVideo.displayName = 'LazyVideo';

const MOBILE_BREAKPOINT = 768;
const MOBILE_ZOOM_SCALE = 1.6;
const DESKTOP_ZOOM_SCALE = 0.8;
const CAROUSEL_FULLSCREEN_ZOOM_SCALE = 2.4;
const PAN_STEP_PX = 24;
const ZOOM_STEP = 0.2;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const SCROLL_SYNC_DEBOUNCE_MS = 80;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = globalThis.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

function usePreloadZoomImages(images: ProductGalleryImage[], currentIndex: number) {
  const preloadedUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const indicesToPreload = [currentIndex, currentIndex - 1, currentIndex + 1];
      indicesToPreload.forEach(idx => {
        const img = images[idx];
        if (img?.zoomUrl && !img.isVideo && !preloadedUrls.current.has(img.zoomUrl)) {
          const preloadImg = new Image();
          preloadImg.src = img.zoomUrl;
          preloadedUrls.current.add(img.zoomUrl);
        }
      });
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [images, currentIndex]);
}

function ZoomInPlusIcon() {
  return <ZoomIn {...defaultIconProps} size={28} strokeWidth={1.5} />;
}

function FullscreenCloseIcon() {
  return <X {...defaultIconProps} size={22} strokeWidth={1.5} />;
}

interface ProductGalleryMobileFullscreenZoomProps {
  image: ProductGalleryImage;
  isOpen: boolean;
  onClose: () => void;
  labels: ReturnType<typeof resolveGalleryLabels>;
}

function ProductGalleryMobileFullscreenZoom({
  image,
  isOpen,
  onClose,
  labels,
}: ProductGalleryMobileFullscreenZoomProps) {
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(CAROUSEL_FULLSCREEN_ZOOM_SCALE);
  const panRef = useRef(pan);
  const scaleRef = useRef(scale);
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  panRef.current = pan;
  scaleRef.current = scale;

  useEffect(() => {
    if (!isOpen) return;
    setPan({ x: 0, y: 0 });
    setScale(CAROUSEL_FULLSCREEN_ZOOM_SCALE);
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    lockBodyScroll();
    closeButtonRef.current?.focus();
    return () => {
      unlockBodyScroll();
      previousFocusRef.current?.focus();
    };
  }, [isOpen, image.key]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPan((current) => ({ ...current, x: current.x + PAN_STEP_PX }));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPan((current) => ({ ...current, x: current.x - PAN_STEP_PX }));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setPan((current) => ({ ...current, y: current.y + PAN_STEP_PX }));
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        setPan((current) => ({ ...current, y: current.y - PAN_STEP_PX }));
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setScale((current) => Math.min(current + ZOOM_STEP, MAX_ZOOM));
      } else if (event.key === '-') {
        event.preventDefault();
        setScale((current) => Math.max(current - ZOOM_STEP, MIN_ZOOM));
      } else if (event.key === 'Tab' && overlayRef.current) {
        const focusable = overlayRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    setPan({
      x: dragRef.current.panX + dx,
      y: dragRef.current.panY + dy,
    });
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  const zoomSrc = image.zoomUrl || image.url;

  return createPortal(
    <FullscreenZoomOverlay
      ref={overlayRef}
      $isOpen={isOpen}
      role="dialog"
      aria-modal={isOpen ? 'true' : undefined}
      aria-hidden={!isOpen}
      {...(!isOpen ? ({ inert: '' } as HTMLAttributes<HTMLDivElement>) : {})}
      aria-label={labels.productImageZoom}
    >
      <FullscreenZoomControls>
        <FullscreenZoomControlButton
          type="button"
          aria-label={labels.zoomOut}
          onClick={() => setScale((current) => Math.max(current - ZOOM_STEP, MIN_ZOOM))}
        >
          −
        </FullscreenZoomControlButton>
        <FullscreenZoomControlButton
          type="button"
          aria-label={labels.zoomIn}
          onClick={() => setScale((current) => Math.min(current + ZOOM_STEP, MAX_ZOOM))}
        >
          +
        </FullscreenZoomControlButton>
      </FullscreenZoomControls>
      <FullscreenZoomStage
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <FullscreenZoomPanLayer $x={pan.x} $y={pan.y}>
          <FullscreenZoomImage
            $scale={scale}
            src={zoomSrc}
            alt={image.alt || labels.productImageZoomed}
            draggable={false}
          />
        </FullscreenZoomPanLayer>
      </FullscreenZoomStage>
      <FullscreenZoomCloseButton
        ref={closeButtonRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        aria-label={labels.closeZoom}
      >
        <FullscreenCloseIcon />
        {labels.closeZoom}
      </FullscreenZoomCloseButton>
    </FullscreenZoomOverlay>,
    document.body,
  );
}

function renderThumbnailContent(
  image: ProductGalleryImage,
  index: number,
  hasFailed: boolean,
  onError: (key: string) => void,
) {
  if (hasFailed) {
    return (
      <ThumbnailPlaceholder>
        <PlaceholderIcon />
      </ThumbnailPlaceholder>
    );
  }

  if (image.isVideo) {
    return (
      <ThumbnailVideo
        src={image.url}
        autoPlay
        muted
        loop
        playsInline
      />
    );
  }

  return (
    <ThumbnailImage
      src={image.url}
      alt={image.alt || `Product thumbnail ${index + 1}`}
      loading={index < 4 ? 'eager' : 'lazy'}
      onError={() => onError(image.key)}
    />
  );
}

function renderMainContent(
  activeImage: ProductGalleryImage | undefined,
  hasFailed: boolean,
  onError: (key: string) => void,
  zoomConfig?: ZoomConfig,
  isMobile?: boolean,
  slideActive = true,
  isMounted = true,
) {
  if (!activeImage) {
    return (
      <ImagePlaceholder>
        <PlaceholderIcon />
      </ImagePlaceholder>
    );
  }

  if (activeImage.isVideo) {
    if (isExternalVideoUrl(activeImage.url)) {
      return (
        <StyledIframe
          src={getEmbedUrl(activeImage.url) || activeImage.url}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          title={activeImage.alt || 'Product video'}
          loading="lazy"
        />
      );
    }
    return (
      <LazyVideo
        src={activeImage.url}
        poster={activeImage.posterUrl}
        isActive={slideActive}
        shouldLoad={slideActive}
      />
    );
  }

  if (hasFailed) {
    return (
      <ImagePlaceholder>
        <PlaceholderIcon />
      </ImagePlaceholder>
    );
  }

  // react-inner-image-zoom is externalized (not Vite-bundled) for SSR, so Node's
  // native require() hands `styled()` its raw CJS module.exports object instead
  // of the unwrapped forwardRef component, crashing renderToString with
  // "Element type is invalid ... got: object". Render the plain image on the
  // server (and on the client's first/hydration render, to match the server
  // markup) and upgrade to the zoomable version once mounted in the browser.
  if (zoomConfig?.enabled && isMounted) {
    return (
      <StyledInnerImageZoom
        src={activeImage.url}
        zoomSrc={activeImage.zoomUrl || activeImage.url}
        zoomType={isMobile ? 'click' : (zoomConfig.zoomType || 'hover')}
        hideHint={zoomConfig.hideHint}
        zoomScale={isMobile ? MOBILE_ZOOM_SCALE : (zoomConfig.zoomScale || DESKTOP_ZOOM_SCALE)}
        fullscreenOnMobile={isMobile}
        className="zoomWrapper"
        imgAttributes={{
          alt: activeImage.alt || 'Product image',
          style: { objectFit: 'cover' },
        }}
      />
    );
  }

  return (
    <MainImage
      src={activeImage.url}
      alt={activeImage.alt || 'Product image'}
      loading="eager"
      onError={() => onError(activeImage.key)}
    />
  );
}

interface ProductGalleryCarouselLayoutProps {
  validImages: ProductGalleryImage[];
  safeActiveIndex: number;
  setActiveIndex: (index: number) => void;
  failedImages: Set<string>;
  handleImageError: (imageKey: string) => void;
  topTag?: React.ReactNode;
  topRightTag?: React.ReactNode;
  bottomTag?: React.ReactNode;
  zoomConfig?: ZoomConfig;
  labels: ReturnType<typeof resolveGalleryLabels>;
}

function ProductGalleryCarouselLayout({
  validImages,
  safeActiveIndex,
  setActiveIndex,
  failedImages,
  handleImageError,
  topTag,
  topRightTag,
  bottomTag,
  zoomConfig,
  labels,
}: ProductGalleryCarouselLayoutProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [fullscreenZoomOpen, setFullscreenZoomOpen] = useState(false);

  const carouselZoomEnabled = zoomConfig?.enabled ?? false;
  const activeSlideImage = validImages[safeActiveIndex];
  const activeSlideFailed = activeSlideImage
    ? failedImages.has(activeSlideImage.key)
    : true;
  const showZoomButton =
    carouselZoomEnabled &&
    Boolean(activeSlideImage) &&
    !activeSlideFailed &&
    !activeSlideImage?.isVideo;

  useEffect(() => {
    setFullscreenZoomOpen(false);
  }, [safeActiveIndex]);

  const scrollToIndex = useCallback((index: number, behavior: ScrollBehavior = 'smooth') => {
    const viewport = viewportRef.current;
    if (!viewport || viewport.clientWidth <= 0) return;
    const clamped = Math.min(Math.max(index, 0), validImages.length - 1);
    const targetLeft = clamped * viewport.clientWidth;
    if (Math.abs(viewport.scrollLeft - targetLeft) < 4) return;
    viewport.scrollTo({ left: targetLeft, behavior });
  }, [validImages.length]);

  useLayoutEffect(() => {
    scrollToIndex(safeActiveIndex, 'smooth');
  }, [safeActiveIndex, scrollToIndex]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const syncIndexFromScroll = () => {
      const width = viewport.clientWidth;
      if (width <= 0) return;
      const next = Math.min(
        validImages.length - 1,
        Math.max(0, Math.round(viewport.scrollLeft / width)),
      );
      if (next !== safeActiveIndex) {
        setActiveIndex(next);
      }
    };

    viewport.addEventListener('scrollend', syncIndexFromScroll);
    let scrollTimeoutId: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(scrollTimeoutId);
      scrollTimeoutId = setTimeout(syncIndexFromScroll, SCROLL_SYNC_DEBOUNCE_MS);
    };
    viewport.addEventListener('scroll', onScroll);

    const resizeObserver = new ResizeObserver(() => {
      scrollToIndex(safeActiveIndex, 'auto');
    });
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener('scrollend', syncIndexFromScroll);
      viewport.removeEventListener('scroll', onScroll);
      clearTimeout(scrollTimeoutId);
      resizeObserver.disconnect();
    };
  }, [safeActiveIndex, setActiveIndex, scrollToIndex, validImages.length]);

  const carouselBody = (
    <CarouselRoot>
      <GallerySlideAnnouncer aria-live="polite" aria-atomic="true">
        {labels.viewImage(safeActiveIndex + 1, validImages.length)}
      </GallerySlideAnnouncer>
      <CarouselGalleryFrame>
        <CarouselViewport
          ref={viewportRef}
          aria-roledescription="carousel"
          aria-label={labels.productImages}
        >
          {validImages.map((image, index) => {
            const isActive = index === safeActiveIndex;
            const hasFailed = failedImages.has(image.key);

            return (
              <CarouselSlide
                key={image.key}
                role="group"
                aria-roledescription="slide"
                aria-label={labels.viewImage(index + 1, validImages.length)}
                aria-hidden={!isActive}
                {...(!isActive ? ({ inert: '' } as HTMLAttributes<HTMLDivElement>) : {})}
              >
                <MainImageContainer>
                  {renderMainContent(image, hasFailed, handleImageError, undefined, true, isActive)}
                  {isActive && topTag && <TagContainer position="top">{topTag}</TagContainer>}
                  {isActive && topRightTag && (
                    <TagContainer position="topRight">{topRightTag}</TagContainer>
                  )}
                  {isActive && bottomTag && (
                    <TagContainer position="bottom">{bottomTag}</TagContainer>
                  )}
                </MainImageContainer>
              </CarouselSlide>
            );
          })}
        </CarouselViewport>
        {carouselZoomEnabled && (
          <>
            {activeSlideImage && !activeSlideImage.isVideo && (
              <img
                src={activeSlideImage.zoomUrl || activeSlideImage.url}
                alt=""
                aria-hidden
                fetchPriority="low"
                style={{
                  position: 'absolute',
                  width: 0,
                  height: 0,
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              />
            )}
            <CarouselZoomButton
              type="button"
              $visible={showZoomButton}
              aria-label={labels.zoomInOnProductImage}
              aria-hidden={!showZoomButton}
              onClick={(event) => {
                event.stopPropagation();
                if (!showZoomButton) return;
                setFullscreenZoomOpen(true);
              }}
            >
              <ZoomInPlusIcon />
              {labels.zoomIn}
            </CarouselZoomButton>
          </>
        )}
        {validImages.length > 1 && (
          <CarouselDots role="group" aria-label={labels.productImagePagination}>
            {validImages.map((image, index) => (
              <CarouselDotHitArea
                key={image.key}
                type="button"
                aria-current={index === safeActiveIndex ? 'true' : undefined}
                aria-label={labels.viewImage(index + 1, validImages.length)}
                $active={index === safeActiveIndex}
                onClick={() => setActiveIndex(index)}
              >
                <CarouselDot $active={index === safeActiveIndex} aria-hidden />
              </CarouselDotHitArea>
            ))}
          </CarouselDots>
        )}
      </CarouselGalleryFrame>
      {carouselZoomEnabled && validImages.length > 0 && (
        <ProductGalleryMobileFullscreenZoom
          image={activeSlideImage ?? validImages[0]}
          isOpen={fullscreenZoomOpen}
          onClose={() => setFullscreenZoomOpen(false)}
          labels={labels}
        />
      )}
    </CarouselRoot>
  );

  return carouselBody;
}

export function ProductGallery({
  images,
  selectedValue,
  topTag,
  topRightTag,
  bottomTag,
  thumbnailPosition = 'vertical',
  layout = 'thumbnails',
  hasToPreserveOrder = false,
  zoomConfig,
  labels: labelsProp,
}: ProductGalleryProps) {
  const labels = resolveGalleryLabels(labelsProp);
  const isMobile = useIsMobile();
  // Track which images have failed to load
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  // react-inner-image-zoom is only safe to render client-side (see renderMainContent
  // usage below) — start false so hydration matches the server-rendered plain image,
  // then flip after mount to enable zoom in the browser.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Filter out images with empty/invalid URLs upfront
  const validImages = useMemo(() => {
    return images.filter(img => {
      // Check if URL exists and is not empty
      if (!img.url || typeof img.url !== 'string') return false;
      const trimmedUrl = img.url.trim();
      if (!trimmedUrl) return false;
      // Check if it's not just a placeholder or invalid URL
      if (trimmedUrl === '#' || trimmedUrl === 'undefined' || trimmedUrl === 'null') return false;
      return true;
    });
  }, [images]);

  const getInitialIndex = useCallback(() => {
    if (selectedValue && validImages.length > 0) {
      const index = validImages.findIndex(img => img.key === selectedValue.key);
      return index > -1 ? index : 0;
    }
    return 0;
  }, [selectedValue, validImages]);

  const [activeIndex, setActiveIndex] = useState(getInitialIndex());

  useEffect(() => {
    if (selectedValue && !hasToPreserveOrder && validImages.length > 0) {
      const index = validImages.findIndex(img => img.key === selectedValue.key);
      if (index > -1) {
        setActiveIndex(index);
      }
    }
  }, [selectedValue, hasToPreserveOrder, validImages]);

  // Reset failed images when images prop changes
  useEffect(() => {
    setFailedImages(new Set());
  }, [images]);

  // Handle image load error
  const handleImageError = useCallback((imageKey: string) => {
    setFailedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(imageKey);
      return newSet;
    });
  }, []);

  // Ensure activeIndex is within bounds
  const safeActiveIndex = Math.min(activeIndex, Math.max(validImages.length - 1, 0));
  const activeImage = validImages[safeActiveIndex];
  const activeImageFailed = activeImage ? failedImages.has(activeImage.key) : false;

  usePreloadZoomImages(validImages, safeActiveIndex);

  // Return null if no valid images
  if (!validImages || validImages.length === 0) {
    return null;
  }

  if (layout === 'carousel') {
    return (
      <ProductGalleryCarouselLayout
        validImages={validImages}
        safeActiveIndex={safeActiveIndex}
        setActiveIndex={setActiveIndex}
        failedImages={failedImages}
        handleImageError={handleImageError}
        topTag={topTag}
        topRightTag={topRightTag}
        bottomTag={bottomTag}
        zoomConfig={zoomConfig}
        labels={labels}
      />
    );
  }

  // Check if we have any non-failed images to show thumbnails
  const hasValidThumbnails = validImages.some(img => !failedImages.has(img.key));
  const showThumbnails = validImages.length > 1 && hasValidThumbnails;

  const galleryContent = (
    <GalleryContainer thumbnailPosition={thumbnailPosition} showThumbnails={showThumbnails}>
      {/* Thumbnails - only show if we have multiple images or at least one valid thumbnail */}
      {showThumbnails && (
        <ThumbnailList
          thumbnailPosition={thumbnailPosition}
          role="list"
          aria-label={labels.productImages}
        >
          {validImages.map((image, index) => {
            const hasFailed = failedImages.has(image.key);

            return (
              <ThumbnailButton
                key={image.key}
                isActive={safeActiveIndex === index}
                onClick={() => setActiveIndex(index)}
                aria-label={labels.viewImage(index + 1, validImages.length)}
                aria-current={safeActiveIndex === index}
                role="listitem"
              >
                {renderThumbnailContent(image, index, hasFailed, handleImageError)}
              </ThumbnailButton>
            );
          })}
        </ThumbnailList>
      )}

      {/* Main Image or Video */}
      <MainImageContainer>
        {renderMainContent(activeImage, activeImageFailed, handleImageError, zoomConfig, isMobile, true, isMounted)}

        {/* Tags */}
        {topTag && <TagContainer position="top">{topTag}</TagContainer>}
        {topRightTag && <TagContainer position="topRight">{topRightTag}</TagContainer>}
        {bottomTag && <TagContainer position="bottom">{bottomTag}</TagContainer>}
      </MainImageContainer>
    </GalleryContainer>
  );

  if (zoomConfig?.enabled) {
    return <ImageZoomContainer>{galleryContent}</ImageZoomContainer>;
  }

  return galleryContent;
}
